import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Accessibility, BadgeCheck, CalendarDays, Carrot, CheckCircle2, Leaf, Pencil, Phone, Plus,
  StickyNote, Trash2, UserRound, Users, WheatOff, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { publicApi } from '../lib/api';
import { BrandMark } from '../components/BrandMark';

/**
 * דף איסוף אורחים (המנגנון מ-DoWe) — בן משפחה מקבל לינק אישי,
 * מזין את האורחים שלו לאירוע, ורואה/עורך את מי שכבר הזין.
 */

type Entry = {
  id: string;
  guest_name: string;
  guest_phone: string;
  attending_count: number | null;
  status: string;
  created_at: string;
  dietary_notes: string | null;
  accessibility_notes: string | null;
};

/** אפשרויות תזונה מהירות — נשמרות ב-dietary_notes, לכל אחת אייקון משלה */
const DIET_OPTIONS: { label: string; Icon: LucideIcon }[] = [
  { label: 'טבעוני',    Icon: Leaf },
  { label: 'צמחוני',    Icon: Carrot },
  { label: 'כשר',       Icon: BadgeCheck },
  { label: 'ללא גלוטן', Icon: WheatOff },
];
const DIET_LABELS = DIET_OPTIONS.map((o) => o.label);
/** תווית הנגישות — נשמרת ב-accessibility_notes (יחד עם הערות מיוחדות) */
const ACCESS_LABEL = 'נגישות לכיסא גלגלים';
const SEP = ' · ';

/** מפרק accessibility_notes חזרה ל: דגל נגישות + הערות מיוחדות */
function parseAccess(raw: string | null): { wheelchair: boolean; notes: string } {
  const t = (raw || '').trim();
  if (!t) return { wheelchair: false, notes: '' };
  const parts = t.split(SEP).map((p) => p.trim()).filter(Boolean);
  const wheelchair = parts.includes(ACCESS_LABEL);
  return { wheelchair, notes: parts.filter((p) => p !== ACCESS_LABEL).join(SEP) };
}

function buildAccess(wheelchair: boolean, notes: string): string | null {
  const parts = [wheelchair ? ACCESS_LABEL : '', notes.trim()].filter(Boolean);
  return parts.length ? parts.join(SEP) : null;
}

type Meta = {
  event_name: string;
  event_date: string | null;
  max_people: number;
  link_label: string | null;
  entries: Entry[];
};

function formatDate(date: string | null): string {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat('he-IL', {
      day: 'numeric', month: 'long', year: 'numeric', weekday: 'long',
    }).format(new Date(date));
  } catch {
    return date;
  }
}

export default function GuestCollectionPage() {
  const { token = '' } = useParams();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [count, setCount] = useState(1);
  const [diets, setDiets] = useState<string[]>([]);
  const [wheelchair, setWheelchair] = useState(false);
  const [specialNotes, setSpecialNotes] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    let alive = true;
    publicApi
      .get(`/public/guest-collection/${token}`)
      .then((r) => alive && setMeta(r.data))
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [token]);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(''), 3000);
  }

  function resetForm() {
    setName('');
    setPhone('');
    setCount(1);
    setDiets([]);
    setWheelchair(false);
    setSpecialNotes('');
    setEditingId(null);
    setError('');
  }

  function toggleDiet(d: string) {
    setDiets((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  function startEdit(entry: Entry) {
    setEditingId(entry.id);
    setName(entry.guest_name);
    setPhone(entry.guest_phone);
    setCount(entry.attending_count ?? 1);
    const dietParts = (entry.dietary_notes || '').split(SEP).map((p) => p.trim()).filter(Boolean);
    setDiets(dietParts.filter((p) => DIET_LABELS.includes(p)));
    const acc = parseAccess(entry.accessibility_notes);
    setWheelchair(acc.wheelchair);
    // הערות מיוחדות = הערות חופשיות מהנגישות + תזונה חופשית
    const freeDiet = dietParts.filter((p) => !DIET_LABELS.includes(p)).join(SEP);
    setSpecialNotes([acc.notes, freeDiet].filter(Boolean).join(SEP));
    setError('');
    document.getElementById('gc-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError('שם מלא וטלפון הם שדות חובה');
      return;
    }
    setSubmitting(true);
    setError('');
    const payload = {
      guest_name: name.trim(),
      phone: phone.trim(),
      guest_count: count,
      dietary_notes: diets.length ? diets.join(SEP) : null,
      accessibility_notes: buildAccess(wheelchair, specialNotes),
    };
    try {
      if (editingId) {
        const r = await publicApi.patch(`/public/guest-collection/${token}/entries/${editingId}`, payload);
        setMeta((m) => m && ({
          ...m,
          entries: m.entries.map((en) => (en.id === editingId ? r.data.entry : en)),
        }));
        showToast('האורח עודכן');
      } else {
        const r = await publicApi.post(`/public/guest-collection/${token}`, payload);
        setMeta((m) => m && ({ ...m, entries: [r.data.entry, ...m.entries] }));
        showToast('האורח נוסף בהצלחה!');
      }
      resetForm();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'שגיאה בשמירה');
    } finally {
      setSubmitting(false);
    }
  }

  async function removeEntry(id: string) {
    if (!window.confirm('להסיר את האורח מהרשימה?')) return;
    try {
      await publicApi.delete(`/public/guest-collection/${token}/entries/${id}`);
      setMeta((m) => m && ({ ...m, entries: m.entries.filter((en) => en.id !== id) }));
      if (editingId === id) resetForm();
      showToast('האורח הוסר');
    } catch {
      setError('לא ניתן להסיר את האורח');
    }
  }

  if (loading) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6" role="status" aria-live="polite">
        <div className="text-center space-y-3">
          <div className="mx-auto h-10 w-10 rounded-full border-2 border-sage-200 border-t-sage-600 animate-spin" />
          <p className="text-ink-mute text-sm">טוען…</p>
        </div>
      </div>
    );
  }

  if (notFound || !meta) {
    return (
      <div dir="rtl" className="min-h-full grid place-items-center p-6">
        <div className="text-center max-w-sm surface p-8">
          <h1 className="font-display text-2xl font-bold text-ink">הקישור לא נמצא</h1>
          <p className="text-ink-mute mt-2 text-sm">ייתכן שהקישור שגוי או שפג תוקף — בדקו עם מי ששלח לכם אותו.</p>
        </div>
      </div>
    );
  }

  const totalPeople = meta.entries.reduce((s, en) => s + (en.attending_count ?? 1), 0);

  return (
    <div dir="rtl" className="min-h-full px-4 py-8">
      <div className="mx-auto w-full max-w-lg space-y-5 page-enter">
        {/* כותרת */}
        <header className="text-center space-y-2">
          <BrandMark className="mx-auto" />
          <h1 className="font-display text-3xl font-bold text-ink">{meta.event_name}</h1>
          {meta.event_date && (
            <p className="text-ink-mute text-sm inline-flex items-center gap-1.5">
              <CalendarDays size={15} aria-hidden />
              {formatDate(meta.event_date)}
            </p>
          )}
          {meta.link_label && (
            <p className="inline-flex items-center gap-1.5 rounded-full bg-sage-100 text-sage-800 text-[13px] font-semibold px-3 py-1 mx-auto w-fit">
              <UserRound size={14} aria-hidden />
              הרשימה של {meta.link_label}
            </p>
          )}
          <p className="text-ink-mute text-[14px] leading-relaxed max-w-sm mx-auto">
            הוסיפו כאן את האורחים שלכם לאירוע — כל אורח שתזינו ייכנס ישירות לרשימת המוזמנים.
          </p>
        </header>

        {/* Toast */}
        {toast && (
          <div className="rounded-xl bg-sage-50 border border-sage-200 text-sage-800 text-sm font-semibold px-4 py-2.5 flex items-center gap-2" role="status">
            <CheckCircle2 size={16} aria-hidden />
            {toast}
          </div>
        )}

        {/* טופס הוספה / עריכה */}
        <form id="gc-form" onSubmit={submit} className="surface p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-ink text-[16px]">
              {editingId ? 'עריכת אורח' : 'הוספת אורח'}
            </h2>
            {editingId && (
              <button type="button" onClick={resetForm} className="text-ink-faint hover:text-ink inline-flex items-center gap-1 text-[13px]">
                <X size={14} aria-hidden /> ביטול עריכה
              </button>
            )}
          </div>

          <div>
            <label htmlFor="gc-name" className="block text-[13px] font-semibold text-ink mb-1.5">שם מלא *</label>
            <input
              id="gc-name" className="field w-full" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="ישראל ישראלי" required
            />
          </div>

          <div>
            <label htmlFor="gc-phone" className="block text-[13px] font-semibold text-ink mb-1.5">טלפון נייד *</label>
            <input
              id="gc-phone" className="field w-full" value={phone} inputMode="tel" dir="ltr"
              onChange={(e) => setPhone(e.target.value)} placeholder="050-0000000" required
            />
          </div>

          <div>
            <label className="block text-[13px] font-semibold text-ink mb-1.5">
              כמה אנשים מגיעים? (עד {meta.max_people})
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn-secondary h-11 w-11 !p-0 grid place-items-center text-lg"
                onClick={() => setCount((c) => Math.max(1, c - 1))}
                aria-label="פחות"
              >
                −
              </button>
              <input
                type="number"
                min={1}
                max={meta.max_people}
                inputMode="numeric"
                className="field w-20 text-center font-display text-2xl font-bold text-ink !py-2"
                value={count}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isFinite(n)) {
                    setCount(1);
                    return;
                  }
                  setCount(Math.min(meta.max_people, Math.max(1, n)));
                }}
                aria-label="מספר אנשים"
              />
              <button
                type="button"
                className="btn-secondary h-11 w-11 !p-0 grid place-items-center text-lg"
                onClick={() => setCount((c) => Math.min(meta.max_people, c + 1))}
                aria-label="יותר"
              >
                +
              </button>
            </div>
          </div>

          {/* העדפות תזונה */}
          <div>
            <label className="block text-[13px] font-semibold text-ink mb-1.5 inline-flex items-center gap-1.5">
              <Leaf size={14} className="text-sage-700" aria-hidden />
              העדפות תזונה (אופציונלי)
            </label>
            <div className="flex flex-wrap gap-2">
              {DIET_OPTIONS.map(({ label, Icon }) => {
                const on = diets.includes(label);
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleDiet(label)}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold border transition ${
                      on
                        ? 'bg-sage-600 border-sage-600 text-white'
                        : 'bg-white border-line-strong text-ink-mute hover:border-sage-400'
                    }`}
                  >
                    <Icon size={14} aria-hidden />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* נגישות */}
          <button
            type="button"
            onClick={() => setWheelchair((w) => !w)}
            aria-pressed={wheelchair}
            className={`w-full flex items-center gap-3 rounded-2xl border p-3.5 text-right transition ${
              wheelchair ? 'bg-sage-50 border-sage-400' : 'bg-white border-line-strong hover:border-sage-300'
            }`}
          >
            <span className={`grid h-10 w-10 place-items-center rounded-xl ${wheelchair ? 'bg-sage-600 text-white' : 'bg-sage-50 text-sage-700'}`}>
              <Accessibility size={19} aria-hidden />
            </span>
            <span className="flex-1">
              <span className="block text-[14px] font-semibold text-ink">נדרשת נגישות לכיסא גלגלים</span>
              <span className="block text-[12px] text-ink-mute">נדאג למקום ישיבה מותאם</span>
            </span>
            <span className={`grid h-6 w-6 place-items-center rounded-lg border ${wheelchair ? 'bg-sage-600 border-sage-600 text-white' : 'border-line-strong text-transparent'}`}>
              <CheckCircle2 size={15} aria-hidden />
            </span>
          </button>

          {/* הערות מיוחדות */}
          <div>
            <label htmlFor="gc-notes" className="block text-[13px] font-semibold text-ink mb-1.5 inline-flex items-center gap-1.5">
              <StickyNote size={14} className="text-sage-700" aria-hidden />
              הערות מיוחדות (אופציונלי)
            </label>
            <textarea
              id="gc-notes"
              className="field w-full min-h-[80px] resize-y"
              value={specialNotes}
              onChange={(e) => setSpecialNotes(e.target.value)}
              placeholder="אלרגיות, ישיבה ליד…, עגלת תינוק, וכל דבר שחשוב שנדע"
              maxLength={1000}
            />
          </div>

          {error && <p className="text-red-700 text-sm font-medium" role="alert">{error}</p>}

          <button type="submit" className="btn-primary w-full inline-flex items-center justify-center gap-2" disabled={submitting}>
            {submitting ? 'שומר…' : editingId ? 'שמירת שינויים' : (<><Plus size={17} aria-hidden /> הוספת אורח</>)}
          </button>
        </form>

        {/* האורחים שהוזנו */}
        {meta.entries.length > 0 && (
          <section className="surface p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-ink text-[16px] inline-flex items-center gap-2">
                <Users size={17} className="text-sage-700" aria-hidden />
                האורחים שהזנתם
              </h2>
              <span className="text-[13px] text-ink-mute">
                {meta.entries.length} הזמנות · {totalPeople} אנשים
              </span>
            </div>

            <ul className="divide-y divide-line">
              {meta.entries.map((en) => (
                <li key={en.id} className="py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink text-[15px] truncate">{en.guest_name}</p>
                    <p className="text-[13px] text-ink-mute inline-flex items-center gap-1" dir="ltr">
                      <Phone size={12} aria-hidden />
                      {en.guest_phone}
                    </p>
                    {(en.dietary_notes || en.accessibility_notes) && (
                      <p className="text-[12px] text-sage-800 mt-0.5 inline-flex items-center gap-1 flex-wrap">
                        {(en.dietary_notes || '').split(SEP).map((p) => p.trim()).filter(Boolean).map((part) => {
                          const Opt = DIET_OPTIONS.find((o) => o.label === part)?.Icon || Leaf;
                          return (
                            <span key={part} className="inline-flex items-center gap-1 rounded-full bg-sage-50 px-2 py-0.5">
                              <Opt size={11} aria-hidden />{part}
                            </span>
                          );
                        })}
                        {en.accessibility_notes && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-sage-50 px-2 py-0.5">
                            <Accessibility size={11} aria-hidden />{en.accessibility_notes}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <span className="text-[13px] text-ink-mute whitespace-nowrap">
                    {en.attending_count ?? 1} אנשים
                  </span>
                  <button
                    type="button" onClick={() => startEdit(en)}
                    className="h-9 w-9 grid place-items-center rounded-xl text-ink-faint hover:text-ink hover:bg-sage-50 transition"
                    aria-label={`עריכת ${en.guest_name}`}
                  >
                    <Pencil size={15} aria-hidden />
                  </button>
                  <button
                    type="button" onClick={() => removeEntry(en.id)}
                    className="h-9 w-9 grid place-items-center rounded-xl text-ink-faint hover:text-red-700 hover:bg-red-50 transition"
                    aria-label={`הסרת ${en.guest_name}`}
                  >
                    <Trash2 size={15} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
